import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { CREATE_OCCASIONAL_SLOT, UPDATE_OCCASIONAL_SLOT } from '@/modules/API/slot/occasionalSlotHandler'
import { useNotification } from '@/modules/context/NotificationContext'
import { NewOccasionalSlot, UpdateOccasionalSlot } from '@/types/Slots'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'

export type Submission =
    | { type: 'add'; slots: NewOccasionalSlot[]; scheduled: boolean }
    | { type: 'edit'; slots: UpdateOccasionalSlot[]; scheduled: boolean }

interface UseOccasionalSlotSubmissionProps {
    onSuccess(): void
}

export function useOccasionalSlotSubmission({ onSuccess }: UseOccasionalSlotSubmissionProps) {
    const [create, { loading: isCreating }] = useMutation(CREATE_OCCASIONAL_SLOT)
    const [update, { loading: isUpdating }] = useMutation(UPDATE_OCCASIONAL_SLOT)
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()
    const { currentDaycare: daycare } = useCurrentDaycareInfos()
    const submit: (submission: Submission) => Promise<void> = async (submission: Submission) => {
        if (submission.type === 'add') {
            await create({
                variables: {
                    daycare_id: daycare.id,
                    occasionalSlots: submission.slots
                },
                onCompleted: () => {
                    showNotificationSuccess(
                        submission.scheduled ? t('scheduleOccasionalSlotSuccess') : t('createOccasionalSlotSuccess')
                    )
                },
                onError: (error) => {
                    if (error.message.split('Message: ')[0] === 'A child must be selected for a slot in the past') {
                        showNotificationError(t('childRequiredForPastSlotError'))
                    } else {
                        showNotificationError(
                            submission.scheduled ? t('scheduleOccasionalSlotError') : t('createOccasionalSlotError')
                        )
                    }
                }
            })
        } else {
            await update({
                variables: {
                    daycare_id: daycare.id,
                    occasionalSlots: submission.slots
                },
                onCompleted: () => {
                    showNotificationSuccess(t('occasionalSlotEdited'))
                },
                onError: (error) => {
                    if (error.message.split('Message: ')[0] === 'A child must be selected for a slot in the past') {
                        showNotificationError(t('childRequiredForPastSlotError'))
                    } else {
                        showNotificationError(t('errorEditedOccasionalSlot'))
                    }
                }
            })
        }
        onSuccess()
    }

    return { submit, loading: isCreating || isUpdating }
}
